/**
 * Slate Theme
 *
 * Professional, neutral gray tones.
 */

import type { AppTheme } from './types';

export const slateTheme: AppTheme = {
  name: 'slate',
  displayName: 'Slate',
  description: 'Professional, modern gray tones',

  antdTheme: {
    token: {
      colorPrimary: '#475569',
      colorSuccess: '#22c55e',
      colorWarning: '#f59e0b',
      colorError: '#ef4444',
      colorInfo: '#3b82f6',
      colorLink: '#3b82f6',

      fontSize: 14,
      fontSizeHeading1: 28,
      fontSizeHeading2: 22,
      fontSizeHeading3: 18,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeightStrong: 600,

      padding: 16,
      paddingLG: 24,
      paddingXL: 32,
      margin: 16,
      marginLG: 24,
      marginXL: 32,

      borderRadius: 6,
      borderRadiusLG: 8,
      borderRadiusSM: 4,
      borderRadiusXS: 3,

      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorBgLayout: '#f8fafc',
      colorBorder: '#e2e8f0',
      colorBorderSecondary: '#f1f5f9',

      colorText: '#1e293b',
      colorTextSecondary: '#475569',
      colorTextTertiary: '#94a3b8',
      colorTextQuaternary: '#cbd5e1',

      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
      boxShadowSecondary: '0 1px 2px rgba(0, 0, 0, 0.03)',
    },

    components: {
      Layout: {
        headerBg: '#334155',
        headerColor: '#ffffff',
        headerHeight: 48,
        headerPadding: '0 20px',
        siderBg: '#1e293b',
        bodyBg: '#f8fafc',
      },

      Button: {
        borderRadius: 6,
        borderRadiusLG: 8,
        borderRadiusSM: 4,
        controlHeight: 36,
        controlHeightLG: 42,
        controlHeightSM: 28,
        fontWeight: 500,
        primaryShadow: 'none',
      },

      Card: {
        borderRadius: 8,
        borderRadiusLG: 10,
        colorBgContainer: '#ffffff',
        boxShadowTertiary: 'none',
        padding: 20,
        paddingLG: 24,
      },

      Table: {
        headerBg: '#f8fafc',
        headerColor: '#94a3b8',
        headerBorderRadius: 6,
        borderRadius: 6,
        rowHoverBg: '#f8fafc',
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
      },

      Input: {
        borderRadius: 6,
        controlHeight: 36,
        controlHeightLG: 42,
      },

      Select: {
        borderRadius: 6,
        controlHeight: 36,
        controlHeightLG: 42,
      },

      Tag: {
        borderRadiusSM: 4,
        fontSizeSM: 12,
      },

      Modal: {
        borderRadiusLG: 10,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
      },

      Drawer: {
        colorBgElevated: '#ffffff',
      },

      Badge: {
        fontSize: 12,
      },

      Breadcrumb: {
        fontSize: 13,
        itemColor: '#94a3b8',
        linkColor: '#3b82f6',
      },

      Segmented: {
        borderRadius: 6,
        itemSelectedBg: '#475569',
        itemSelectedColor: '#ffffff',
      },

      Tabs: {
        itemColor: '#94a3b8',
        itemSelectedColor: '#1e293b',
        itemHoverColor: '#475569',
      },

      Dropdown: {
        borderRadiusLG: 8,
      },

      Tooltip: {
        borderRadius: 6,
      },

      Divider: {
        colorSplit: '#f1f5f9',
      },
    },
  },

  cssVariables: {
    '--layout-header-bg': '#334155',
    '--layout-sidebar-bg': '#1e293b',
    '--layout-content-bg': '#f8fafc',

    '--surface-0': '#f8fafc',
    '--surface-1': '#ffffff',
    '--surface-2': '#ffffff',
    '--surface-3': '#ffffff',

    '--text-primary': '#1e293b',
    '--text-secondary': '#475569',
    '--text-tertiary': '#94a3b8',
    '--text-muted': '#cbd5e1',

    '--border-default': '#e2e8f0',
    '--border-subtle': '#f1f5f9',
    '--border-strong': '#cbd5e1',

    '--border-color': '#e2e8f0',
    '--border-radius': '6px',

    '--shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.03)',
    '--shadow-md': '0 1px 3px rgba(0, 0, 0, 0.04)',
    '--shadow-lg': '0 4px 16px rgba(0, 0, 0, 0.08)',

    '--accent-warm': '#f59e0b',
    '--accent-cool': '#3b82f6',

    '--status-success': '#22c55e',
    '--status-warning': '#f59e0b',
    '--status-error': '#ef4444',
    '--status-info': '#3b82f6',

    '--status-success-subtle': '#ecfdf5',
    '--status-warning-subtle': '#fffbeb',
    '--status-error-subtle': '#fef2f2',
    '--status-info-subtle': '#eff6ff',

    '--control-bg': '#f8fafc',
    '--control-border': '#e2e8f0',
    '--control-hover': '#f1f5f9',

    '--hover-bg': '#f8fafc',
    '--active-bg': '#f1f5f9',
  },
};
